import {
  doc,
  onSnapshot,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import PERMISSION_MAP, { getDefaultType } from "../../Shared/permissionMap";

/* ═══════════════════════════════════════════════════════════════
 *  PERMISSION CONFIG — per-community feature/action toggles & types
 *
 *  Stored in Firestore: community_permissions/{communityCode}
 *
 *  Shape:
 *  {
 *    communityCode,
 *    actions: {
 *      "finance.account.create": { enabled: true, type: "public" },
 *      "finance.account.delete": { enabled: true, type: "restricted" },
 *      ...
 *    },
 *    features: {
 *      "Accounts": { enabled: true },
 *      ...
 *    },
 *    updatedAt: Timestamp,
 *  }
 *
 *  - `actions.type`: "public" | "restricted" | "hidden"
 *  - `features` toggles entire feature groups on/off
 *  - When not present in Firestore, type falls back to defaultType in permissionMap
 * ═══════════════════════════════════════════════════════════════ */

const TABLE = "community_permissions";

/* ── Build default config from PERMISSION_MAP ──── */
export function buildDefaultPermissionConfig() {
  const actions = {};
  const features = {};

  Object.entries(PERMISSION_MAP).forEach(([actionId, entry]) => {
    actions[actionId] = { enabled: true, type: entry.defaultType || "public" };
    if (entry.feature && !features[entry.feature]) {
      features[entry.feature] = { enabled: true };
    }
  });

  return { actions, features };
}

/* ── Read (real-time) ──────────────────────────── */
export function getPermissionConfig(communityCode, cb) {
  return onSnapshot(doc(db, TABLE, communityCode), (snap) => {
    if (snap.exists()) {
      cb({ id: snap.id, ...snap.data() });
    } else {
      // No config yet — return default (all enabled)
      cb(buildDefaultPermissionConfig());
    }
  });
}

/* ── Save / update ─────────────────────────────── */
export async function savePermissionConfig(communityCode, config) {
  await setDoc(doc(db, TABLE, communityCode), {
    communityCode,
    actions: config.actions,
    features: config.features,
    updatedAt: Timestamp.now(),
  });
}

/* ── Initialize defaults for a new community ───── */
export async function initializePermissionConfig(communityCode) {
  const defaults = buildDefaultPermissionConfig();
  await setDoc(doc(db, TABLE, communityCode), {
    communityCode,
    ...defaults,
    updatedAt: Timestamp.now(),
  }, { merge: true }); // merge so we don't overwrite if it already exists
}

/* ── Check if an action is enabled ─────────────── */
export function isActionEnabled(permConfig, actionId) {
  if (!permConfig) return true; // no config = all enabled

  // Check feature-level first
  const entry = PERMISSION_MAP[actionId];
  if (entry?.feature && permConfig.features?.[entry.feature]) {
    if (!permConfig.features[entry.feature].enabled) return false;
  }

  // Check action-level
  if (permConfig.actions?.[actionId]) {
    return permConfig.actions[actionId].enabled !== false;
  }

  return true; // default = enabled
}

/* ── Get the effective type for an action ──────── */
export function getActionType(permConfig, actionId) {
  // Firestore override takes precedence
  if (permConfig?.actions?.[actionId]?.type) {
    return permConfig.actions[actionId].type;
  }
  // Fall back to default from permissionMap
  return getDefaultType(actionId);
}

/* ── Save a single action's type to Firestore ──── */
export async function saveActionType(communityCode, permConfig, actionId, type) {
  const updated = {
    actions: {
      ...permConfig?.actions,
      [actionId]: {
        ...(permConfig?.actions?.[actionId] || {}),
        enabled: type !== "disabled",
        type,
      },
    },
    features: { ...permConfig?.features },
  };
  await savePermissionConfig(communityCode, updated);
}

/* ── Get grouped permission data for UI ────────── */
export function getGroupedPermissions(permConfig) {
  const groups = {};

  Object.entries(PERMISSION_MAP).forEach(([actionId, entry]) => {
    const feature = entry.feature || "Other";
    if (!groups[feature]) {
      groups[feature] = {
        feature,
        area: entry.area,
        enabled: permConfig?.features?.[feature]?.enabled !== false,
        actions: [],
      };
    }
    groups[feature].actions.push({
      actionId,
      label: entry.label,
      area: entry.area,
      enabled: isActionEnabled(permConfig, actionId),
      type: getActionType(permConfig, actionId),
      defaultType: entry.defaultType || "public",
    });
  });

  return Object.values(groups);
}
